import { BaseComponentImpl, RoomImpl } from "../types/implementations";
import { Building } from "../types/protocols";

/**
 * Check if a component belongs to a specific room based on parentSurface relationships
 * Similar to iOS implementation where components can belong to rooms through:
 * 1. Direct roomKey relationship
 * 2. ANY parentSurface field (parentSurface, parentSurface1, or parentSurface2) 
 *    pointing to a surface component that belongs to the room
 * 
 * This ensures that components are properly associated with rooms regardless of 
 * which parentSurface field is used to define the relationship.
 */
export function componentBelongsToRoom(
    component: BaseComponentImpl, 
    room: RoomImpl, 
    building: Building
): boolean {
    // Check direct roomKey relationship
    if (component.roomKey === room.key) {
        return true;
    }

    // Check ALL parentSurface relationships - if ANY parentSurface belongs to the room,
    // then the component belongs to the room
    const parentSurfaces = [
        component.parentSurface?.value,
        component.parentSurface1?.value,
        component.parentSurface2?.value
    ].filter(Boolean); // Remove undefined values

    for (const parentSurfaceKey of parentSurfaces) {
        if (!parentSurfaceKey) continue;

        // Find the parent surface component
        const parentSurfaceComponent = building.model.structure?.components?.[parentSurfaceKey];
        if (!parentSurfaceComponent) continue;

        // Check if the parent surface component belongs to the room
        if (parentSurfaceComponent.roomKey === room.key) {
            return true;
        }
    }

    return false;
}

/**
 * Get all components that belong to a specific room
 */
export function getComponentsForRoom(
    room: RoomImpl,
    building: Building
): BaseComponentImpl[] {
    if (!building.model.structure?.components) {
        return [];
    }

    const components: BaseComponentImpl[] = [];

    Object.values(building.model.structure.components).forEach(componentData => {
        const component = new BaseComponentImpl(componentData);
        
        if (componentBelongsToRoom(component, room, building)) {
            components.push(component);
        }
    });

    return components;
}

/**
 * Get all components with blueprint locations that belong to a specific room
 */
export function getComponentsWithBlueprintLocationsForRoom(
    room: RoomImpl,
    building: Building,
    blueprintKey: string
): BaseComponentImpl[] {
    const components = getComponentsForRoom(room, building);
    
    return components.filter(component => {
        // Check if component has blueprint locations for the specified blueprint
        const blueprintLocations = (component as any).blueprintLocations;
        return blueprintLocations && blueprintLocations[blueprintKey];
    });
}

/**
 * Find the room that a component belongs to based on parentSurface relationships
 * Returns the room key or null if no room is found
 * 
 * Checks ALL parentSurface fields (parentSurface, parentSurface1, parentSurface2)
 * to determine which room the component belongs to.
 */
export function findRoomForComponent(
    component: BaseComponentImpl,
    building: Building
): string | null {
    // Check direct roomKey relationship
    if (component.roomKey) {
        return component.roomKey;
    }

    // Check ALL parentSurface relationships
    const parentSurfaces = [
        component.parentSurface?.value,
        component.parentSurface1?.value,
        component.parentSurface2?.value
    ].filter(Boolean);

    for (const parentSurfaceKey of parentSurfaces) {
        if (!parentSurfaceKey) continue;

        const parentSurfaceComponent = building.model.structure?.components?.[parentSurfaceKey];
        if (parentSurfaceComponent?.roomKey) {
            return parentSurfaceComponent.roomKey;
        }
    }

    return null;
}
